import React, { useState } from "react";
import { connect } from "react-redux";
import validate from "validate.js";
import OnboardImage from "../../components/OnboardImage";
import * as style from "../../components/CandidateJob/styles.module.scss";
import FormBuilder from "../../common/FormBuilder";
import { storeUserData, submitClientFormSilent } from "../../redux/actions";
import { downloadFormAttachmentContent } from "../../api/fontApi";
import history from "../../history";

const generateFieldOptions = (options = []) => {
  return options.map((option) => ({
    value: option.value,
    label: option.value,
  }));
};

const populateState = (fields, formKey, candidateForm) => {
  const candidateFieldsMap = Array.isArray(candidateForm?.fields)
    ? candidateForm.fields.reduce(
        (acc, field) => ({
          ...acc,
          [field.id]: field.value,
        }),
        {}
      )
    : {};

  const formFieldsState = fields.reduce((acc, field) => {
    const candidateFieldValue = candidateFieldsMap[field.id];

    if (candidateFieldValue && typeof candidateFieldValue === "object") {
      const { url, filename, answer } = candidateFieldValue;
      acc[field.id] = {
        uploaded: !!url,
        path: url || "",
        name: filename || "",
        value: answer || "",
      };
    } else {
      acc[field.id] = field.type === "multiple_answers" ? [] : "";
    }

    return acc;
  }, {});

  return {
    [formKey]: {
      fields: formFieldsState,
    },
  };
};

const populateErrorsState = (fields, formKey) => {
  return {
    [formKey]: fields.reduce((acc, field) => {
      acc[field.id] = [];
      return acc;
    }, {}),
  };
};

const updateState = (state, formKey, id, value) => ({
  ...state,
  [formKey]: {
    ...state[formKey],
    fields: {
      ...state[formKey].fields,
      [id]: {
        ...state[formKey].fields[id],
        value,
      },
    },
  },
});

const setStateForMultipleAnswers = (formKey, field, setState) => {
  setState((prevState) => {
    const formState = prevState[formKey] || { fields: {} };
    const fieldState = formState.fields[field.id] || { value: [] };
    const currentValues = Array.isArray(fieldState.value)
      ? fieldState.value
      : [];
    if (field.value == null) return prevState;
    const newValues = currentValues.includes(field.value)
      ? currentValues.filter((v) => v !== field.value)
      : [...currentValues, field.value];
    return updateState(
      {
        ...prevState,
        [formKey]: {
          ...formState,
          fields: {
            ...formState.fields,
            [field.id]: { ...fieldState, value: newValues },
          },
        },
      },
      formKey,
      field.id,
      newValues
    );
  });
};

const resetErrorsState = (setState, formKey, fieldKey) => {
  setState((prevState) => ({
    ...prevState,
    [formKey]: {
      ...prevState[formKey],
      [fieldKey]: [],
    },
  }));
};

const setStateForSingleAnswer = (formKey, field, setState) => {
  setState((prevState) => {
    return updateState(prevState, formKey, field.id, field.value);
  });
};

const useSectionState = (form = {}) => {
  const { fields = [], candidateForm = {}, url } = form;

  const constraints = fields.reduce((acc, field) => {
    acc[field.id] = (value, attributes) => {
      return {
        ...(field.required && {
          presence: {
            allowEmpty: false,
            message: "^This field is required",
          },
          ...(field.type === "statement_confirmation" && {
            inclusion: {
              within: [true, "true"],
              message: "^This field is required",
            },
          }),
        }),
      };
    };
    return acc;
  }, {});

  const initialState = populateState(fields, url, candidateForm);

  const initialErrors = populateErrorsState(fields, url);

  const [state, setState] = useState(initialState);

  const [errors, setErrors] = useState(initialErrors);

  const handleFieldChange = (formKey, field) => {
    resetErrorsState(setErrors, formKey, field.id);
    switch (field.type) {
      case "multiple_answers":
        setStateForMultipleAnswers(formKey, field, setState);
        break;
      default:
        setStateForSingleAnswer(formKey, field, setState);
    }
  };

  const useFieldState = (formKey, field) => {
    const currentField = state[formKey].fields?.[field.id];
    return {
      value:
        currentField?.value || (field.type === "multiple_answers" ? [] : ""),
      options: generateFieldOptions(field.options),
      label: field.question,
      required: field.required,
      onFieldChange: (e) =>
        handleFieldChange(formKey, {
          ...field,
          value: e.target.value,
        }),
      onCheckBoxChange: (e) =>
        handleFieldChange(formKey, {
          ...field,
          value: e.target.checked,
        }),
      onFileChange: (key, fileData) =>
        handleFieldChange(formKey, {
          ...field,
          value: fileData,
        }),
    };
  };
  return { state, setState, useFieldState, constraints, errors, setErrors };
};

const JobFormBuilderContainer = ({
  appData,
  jobId,
  formId,
  section,
  submitForm,
  updateAppState,
  currentJobProgressState,
}) => {
  const { candidateJobs } = appData;
  const { sections } = candidateJobs.find((j) => j.id === jobId);
  const sectionData = sections[section];
  const sectionKeys = Object.keys(sections);
  const sectionIndex = sectionKeys.indexOf(section);
  const { sectionName, forms } = sectionData;
  const formIndex = forms.findIndex((f) => f.url === formId);
  const form = forms.find((f) => f.url === formId);
  const rtwLink = `/profile/edit/job/${jobId}/legal-compliance/rtw`;
  const imagePathName =
    section === "job-requirements"
      ? "/images/job_requirements.svg"
      : "/images/job_onboarding.svg";
  const description = form.introduction || "";

  const imageClassName =
    section === "job-requirements"
      ? style.jobRequirements__image
      : style.jobOnboarding__image;

  const { state, useFieldState, constraints, errors, setErrors } =
    useSectionState(form);

  const handleBack = (e) => {
    e.preventDefault();

    if (formIndex > 0) {
      return history.push(
        `/profile/edit/job/${jobId}/${section}/${forms[formIndex - 1].url}`
      );
    }

    if (sectionIndex <= 0) {
      return history.push(rtwLink);
    }

    const previousSection = sections[sectionKeys[sectionIndex - 1]];
    const lastForm = previousSection.forms.slice(-1)[0];

    return history.push(
      lastForm
        ? `/profile/edit/job/${jobId}/${sectionKeys[sectionIndex - 1]}/${
            lastForm.url
          }`
        : rtwLink
    );
  };

  const handleForward = () => {
    const nextSection = sections[sectionKeys[sectionIndex + 1]];
    const nextSessionFirstForm = nextSection && nextSection.forms[0];
    const stayInSection = formIndex < forms.length - 1;

    updateAppState({
      ...appData,
      candidateJobs: candidateJobs.map((job) => {
        if (job.id === jobId) {
          return {
            ...job,
            sections: {
              ...job.sections,
              [section]: {
                ...sectionData,
                completed: !stayInSection ? true : sectionData.completed,
                forms: forms.map((f, i) => {
                  if (i === formIndex) {
                    return {
                      ...f,
                      completed: true,
                    };
                  }
                  return f;
                }),
              },
            },
          };
        }
        return job;
      }),
    });

    if (stayInSection) {
      return history.push(
        `/profile/edit/job/${jobId}/${section}/${forms[formIndex + 1].url}`
      );
    }

    if (nextSessionFirstForm) {
      return history.push(
        `/profile/edit/job/${jobId}/${sectionKeys[sectionIndex + 1]}/${
          nextSessionFirstForm.url
        }`
      );
    }

    return history.push("/profile");
  };

  const prepareValidationData = (fields) => {
    return Object.keys(fields).reduce((acc, key) => {
      acc[key] = fields[key]?.value;
      return acc;
    }, {});
  };

  const handleSubmit = (id, fieldRefs) => {
    const validationErrors =
      form.timeLimit.enabled || form.signOffAsYouGo
        ? null
        : validate(prepareValidationData(state[id].fields), constraints);

    if (validationErrors) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [id]: {
          ...prevErrors[id],
          ...validationErrors,
        },
      }));

      const firstInvalidField = Object.keys(validationErrors)[0];
      fieldRefs.current[firstInvalidField]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    if (form.timeLimit.enabled && form.completed) {
      handleForward();
      return;
    }

    const formData = new FormData();
    Object.entries(state[id].fields).forEach(([field, answer]) => {
      formData.append(field, answer.value);
    });
    formData.append("versionId", form.id);
    formData.append("jobKey", jobId);

    submitForm(formData);

    handleForward();
  };

  const downloadAttachmentContent = () => {
    return downloadFormAttachmentContent(form.url);
  };

  return (
    <div className="row h-100">
      <OnboardImage
        title={form.name}
        imagePath={imagePathName}
        className={style.imageBackground}
        imageClassName={imageClassName}
        innerProgressTitle={sectionName}
        innerProgressCurrentStep={formIndex + 1}
        innerProgressTotalSteps={forms.length}
        desktopDescription={description}
        mobileDescription={description}
        innerProgressShow
        currentJobProgressState={currentJobProgressState}
      />
      <FormBuilder
        id={formId}
        formSubmitted={form.completed}
        fields={form.fields}
        description={form.description}
        timer={form.timeLimit}
        attachment={form.attachment}
        downloadAttachmentContent={downloadAttachmentContent}
        useFieldState={useFieldState}
        handleSubmit={handleSubmit}
        handleBack={handleBack}
        formStyles={style.form}
        actionStyles={style.actions}
        errors={errors}
      />
    </div>
  );
};
const mapStateToProps = (state) => ({
  appData: state.core.app,
});

const mapDispatchToProps = {
  submitForm: submitClientFormSilent,
  updateAppState: storeUserData,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRedux(JobFormBuilderContainer);
